import { supabase } from './supabase';
import { Session } from '@supabase/supabase-js';

export async function waitForSupabaseSession(timeout = 5000): Promise<Session | null> {
  const start = Date.now();
  while (Date.now() - start < timeout) {
    const { data } = await supabase.auth.getSession();
    if (data.session) return data.session;
    await new Promise(res => setTimeout(res, 200));
  }
  return null;
}

export const checkStorageConsistency = async (session: Session | null): Promise<boolean> => {
  try {
    if (window.location.pathname === "/dev/components") {
      if (import.meta.env.DEV) {
        console.log("🧪 Skipping storage consistency check on dev route");
      }
      return true;
    }

    if (import.meta.env.DEV) {
      if (session?.expires_at) {
        console.log(`⏳ Session expires at: ${new Date(session.expires_at * 1000).toISOString()}`);
      }
    }

    if (session) {
      const now = Date.now();
      const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
      if (now >= expiresAt) {
        console.warn('⚠️ Session expired, signing out...');
        await supabase.auth.signOut();
        window.location.href = '/login';
        return false;
      }
    }

    // Keys that are expected to be plain strings (not JSON)
    const stringKeys = new Set([
      'app.locale',
      'mainCarId',
      'selectedCarId',
      'signup-email',
      'sb-auth-token',
    ]);

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        if (value && !stringKeys.has(key)) {
          // Only try to parse JSON for keys that should contain JSON
        try {
            JSON.parse(value);
        } catch {
          console.warn(`🚫 Corrupted localStorage key: ${key}`);
          await clearStorage();
          return false;
        }
        }
        // stringKeys are assumed to be valid plain strings, skip JSON parsing
      }
    }

    return true;
  } catch (error) {
    console.error('❌ Storage consistency check failed:', error);
    await clearStorage();
    return false;
  }
};

export const clearStorage = async () => {
  console.log('🔍 localStorage before clearing:', { ...localStorage });
  console.log('🧹 Clearing storage...');

  const preserveKeys = new Set([
    'app-supabase-auth',
    'settings',
    'mainCarId',
    'app.locale', // Preserve language preference
    'selectedCarId', // Preserve selected car
  ]);
  for (const key of Object.keys(localStorage)) {
    if (!preserveKeys.has(key)) {
      console.log('🗑️ Removing:', key);
      localStorage.removeItem(key);
    }
  }

  console.log('📦 localStorage after clearing:', { ...localStorage });
};

const handleSessionExpiration = () => {
  localStorage.removeItem('sb-auth-token');
  window.location.href = '/auth/login?message=session_expired';
};
